import { useRef, useState, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * A small mark (✦) whose tooltip says what it means (S8 D42): it opens on hover, on keyboard focus — the mark is a
 * tab stop with a focus ring of its own — and on a tap, where no pointer hovers. The words are in the mark's own text
 * for a screen reader; the tooltip only repeats them for the eye.
 *
 * `focusable={false}` inside a control that is itself the tab stop (the Kanban card is a button): a tab stop inside a
 * button is a control inside a control. That control's name already carries the mark's words, and hover still shows
 * the tooltip.
 */
export function MarkTip({ tip, focusable = true, className, children, ...data }: {
  tip: string; focusable?: boolean; className?: string; children: ReactNode;
} & { [key: `data-${string}`]: string }) {
  const [open, setOpen] = useState(false);
  // Radix closes an open tooltip on pointer down, before the click: the tap toggles from what was there before it.
  const wasOpen = useRef(false);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span
            {...data}
            tabIndex={focusable ? 0 : undefined}
            className={cn(className, focusable
              && 'rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--h-primary)/0.5)]')}
            // A tap toggles it, and is the mark's alone: the row or card under it neither opens nor selects.
            // `preventDefault` keeps Radix from closing what this click just opened.
            onPointerDown={focusable ? () => { wasOpen.current = open; } : undefined}
            onClick={focusable ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              setOpen(!wasOpen.current);
            } : undefined}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
